import iconUrl from "../assets/icon.svg"
import type { Agent, CustomBranding } from "../types/Agent"

type Rgb = { r: number; g: number; b: number }

const PALETTE_TOKENS = [
  "--color-background-bg-brand-primary",
  "--color-background-bg-brand-primary-hover",
  "--color-background-bg-brand-secondary",
  "--color-background-bg-brand-secondary-hover",
  "--color-background-bg-brand-transparent",
  "--color-background-bg-brand-transparent-hover",
  "--color-background-bg-secondary-action",
  "--color-background-bg-secondary-action-hover",
  "--color-border-border-secondary-action",
  "--color-border-border-brand",
  "--color-border-border-brand-hover",
  "--color-foreground-fg-brand-primary",
  "--color-text-text-clicable",
  "--color-text-text-clicable-hover",
  "--color-text-text-on-accent-color",
  "--color-background-bg-component",
  "--color-background-bg-component-hover",
  "--color-background-bg-element",
  "--color-background-bg-button-disabled",
  "--color-border-border-component",
  "--color-border-border-component-active",
  "--color-border-border-divider",
  "--color-border-border-disabled",
  "--color-text-text-inactive",
  "--color-text-text-disabled",
  "--color-background-bg-button-transparent",
  "--color-background-bg-button-transparent-hover",
  "--call-chip-bg",
  "--call-avatar-button-bg",
  "--call-feedback-bg",
  "--call-send-bg",
  "--call-send-fg",
  "--call-bubble-agent-bg",
] as const

const FONT_COLOR_TOKENS = [
  "--color-text-text-title",
  "--color-text-text-label",
  "--color-text-text-paragraph",
  "--color-text-text-subtle",
  "--color-text-text-white",
  "--color-foreground-fg-white",
] as const

const SURFACE_TOKENS = ["--call-brand-background"] as const

const BRAND_TOKENS = [
  ...PALETTE_TOKENS,
  ...FONT_COLOR_TOKENS,
  ...SURFACE_TOKENS,
] as const

type BrandToken = (typeof BRAND_TOKENS)[number]

const WHITE: Rgb = { r: 255, g: 255, b: 255 }
const BLACK: Rgb = { r: 0, g: 0, b: 0 }
const GREY: Rgb = { r: 128, g: 128, b: 128 }
const DARK_TEXT = "#0c0b25"
const LIGHT_TEXT = "#ffffff"

const DEFAULT_TITLE = "Beyond Presence - Talk to a Real-Time AI Avatar"
const BRAND_FONT_LINK_ID = "brand-font"

const MIN_ACCENT_LUMINANCE = 0.22
const TARGET_ACCENT_LUMINANCE = 0.25

const parseColor = (value?: string | null): Rgb | null => {
  if (!value) return null

  const probe = document.createElement("span")
  probe.style.color = value
  const channels = probe.style.color.match(/[\d.]+/g)
  if (!channels || channels.length < 3) return null

  const [r, g, b] = channels.slice(0, 3).map(Number)
  if ([r, g, b].some((channel) => Number.isNaN(channel))) return null
  return { r, g, b }
}

const luminance = ({ r, g, b }: Rgb): number => {
  const channel = (value: number) => {
    const scaled = value / 255
    return scaled <= 0.03928
      ? scaled / 12.92
      : Math.pow((scaled + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

const mix = (color: Rgb, target: Rgb, amount: number): Rgb => ({
  r: Math.round(color.r + (target.r - color.r) * amount),
  g: Math.round(color.g + (target.g - color.g) * amount),
  b: Math.round(color.b + (target.b - color.b) * amount),
})

const toHex = ({ r, g, b }: Rgb): string =>
  `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, "0")).join("")}`

const rgba = ({ r, g, b }: Rgb, alpha: number): string =>
  `rgba(${r}, ${g}, ${b}, ${alpha})`

const hoverOf = (color: Rgb): Rgb =>
  luminance(color) > 0.5 ? mix(color, BLACK, 0.12) : mix(color, WHITE, 0.18)

const readableOn = (color: Rgb): string =>
  luminance(color) > 0.19 ? DARK_TEXT : LIGHT_TEXT

const legibleOnDark = (color: Rgb): Rgb => {
  if (luminance(color) >= MIN_ACCENT_LUMINANCE) return color

  for (let amount = 0.05; amount <= 0.9; amount += 0.05) {
    const lightened = mix(color, WHITE, amount)
    if (luminance(lightened) >= TARGET_ACCENT_LUMINANCE) return lightened
  }
  return mix(color, WHITE, 0.9)
}

const paletteTokens = (
  primary: Rgb,
  secondary: Rgb,
): Record<(typeof PALETTE_TOKENS)[number], string> => {
  const link = legibleOnDark(primary)
  const surface = mix(secondary, BLACK, 0.65)
  const muted = toHex(mix(secondary, GREY, 0.7))

  return {
    "--color-background-bg-brand-primary": toHex(primary),
    "--color-background-bg-brand-primary-hover": toHex(hoverOf(primary)),
    "--color-background-bg-brand-secondary": toHex(secondary),
    "--color-background-bg-brand-secondary-hover": toHex(hoverOf(secondary)),
    "--color-background-bg-brand-transparent": rgba(link, 0.2),
    "--color-background-bg-brand-transparent-hover": rgba(link, 0.12),
    "--color-background-bg-secondary-action": rgba(link, 0.12),
    "--color-background-bg-secondary-action-hover": rgba(link, 0.12),
    "--color-border-border-secondary-action": toHex(link),
    "--color-border-border-brand": toHex(link),
    "--color-border-border-brand-hover": toHex(hoverOf(link)),
    "--color-foreground-fg-brand-primary": toHex(link),
    "--color-text-text-clicable": toHex(link),
    "--color-text-text-clicable-hover": toHex(hoverOf(link)),
    "--color-text-text-on-accent-color": readableOn(primary),
    "--color-background-bg-component": toHex(surface),
    "--color-background-bg-component-hover": rgba(link, 0.12),
    "--color-background-bg-element": toHex(surface),
    "--color-background-bg-button-disabled": toHex(surface),
    "--color-border-border-component": toHex(mix(secondary, BLACK, 0.32)),
    "--color-border-border-component-active": toHex(
      mix(secondary, WHITE, 0.55),
    ),
    "--color-border-border-divider": toHex(mix(secondary, BLACK, 0.58)),
    "--color-border-border-disabled": toHex(mix(secondary, BLACK, 0.5)),
    "--color-text-text-inactive": muted,
    "--color-text-text-disabled": muted,
    "--color-background-bg-button-transparent": rgba(
      mix(secondary, BLACK, 0.35),
      0.6,
    ),
    "--color-background-bg-button-transparent-hover": rgba(
      mix(secondary, BLACK, 0.35),
      0.8,
    ),
    "--call-chip-bg": rgba(mix(secondary, BLACK, 0.78), 0.6),
    "--call-avatar-button-bg": rgba(secondary, 0.5),
    "--call-feedback-bg": rgba(secondary, 0.2),
    "--call-send-bg": toHex(secondary),
    "--call-send-fg": readableOn(secondary),
    "--call-bubble-agent-bg": toHex(mix(secondary, WHITE, 0.9)),
  }
}

const fontColorTokens = (
  font: Rgb,
): Record<(typeof FONT_COLOR_TOKENS)[number], string> => {
  const color = toHex(font)
  return {
    "--color-text-text-title": color,
    "--color-text-text-label": color,
    "--color-text-text-paragraph": color,
    "--color-text-text-subtle": color,
    "--color-text-text-white": color,
    "--color-foreground-fg-white": color,
  }
}

export const safeHttpUrl = (value?: string | null): string | null => {
  const trimmed = value?.trim()
  if (!trimmed) return null

  try {
    const { protocol } = new URL(trimmed, window.location.origin)
    if (protocol !== "http:" && protocol !== "https:") return null
  } catch {
    return null
  }
  return trimmed
}

const brandTokenValues = (
  branding: CustomBranding | null,
): Partial<Record<BrandToken, string>> => {
  if (!branding) return {}

  const brand = parseColor(branding.brand_color)
  const accent = parseColor(branding.accent_color)
  const font = parseColor(branding.font_color)
  const primary = brand ?? accent
  const secondary = accent ?? (primary ? legibleOnDark(primary) : null)
  const background = safeHttpUrl(branding.background_url)

  return {
    ...(primary && secondary ? paletteTokens(primary, secondary) : {}),
    ...(font ? fontColorTokens(font) : {}),
    ...(background
      ? {
          "--call-brand-background": `url("${background.replace(/["\\)]/g, "\\$&")}")`,
        }
      : {}),
  }
}

const applyFont = (branding: CustomBranding | null): void => {
  const href = safeHttpUrl(branding?.font_url)
  const existing = document.getElementById(
    BRAND_FONT_LINK_ID,
  ) as HTMLLinkElement | null

  if (href) {
    const link = existing ?? document.createElement("link")
    link.id = BRAND_FONT_LINK_ID
    link.rel = "stylesheet"
    if (link.getAttribute("href") !== href) {
      link.href = href
    }
    if (!existing) {
      document.head.appendChild(link)
    }
  } else {
    existing?.remove()
  }

  const family = branding?.font_family?.trim()
  if (family) {
    document.documentElement.style.setProperty("--app-font-family", family)
  } else {
    document.documentElement.style.removeProperty("--app-font-family")
  }
}

const applyTitle = (branding: CustomBranding | null): void => {
  document.title = branding?.title?.trim() || DEFAULT_TITLE
}

const applyFavicon = (branding: CustomBranding | null): void => {
  const custom = safeHttpUrl(branding?.favicon_url)
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']")

  if (!link) {
    link = document.createElement("link")
    link.rel = "icon"
    document.head.appendChild(link)
  }

  if (custom) {
    link.removeAttribute("type")
    link.href = custom
  } else {
    link.type = "image/svg+xml"
    link.href = iconUrl
  }
}

export const brandingStyles = (agent?: Agent | null): CustomBranding | null =>
  agent?.custom_branding ?? null

export const applyBranding = (branding: CustomBranding | null): void => {
  const root = document.documentElement
  const values = brandTokenValues(branding)

  BRAND_TOKENS.forEach((token) => root.style.removeProperty(token))
  Object.entries(values).forEach(([token, value]) =>
    root.style.setProperty(token, value),
  )

  applyFont(branding)
  applyTitle(branding)
  applyFavicon(branding)
}
